import { isSSR } from '../next/services/nextjs.rendering'

const COLLAPSE_REGEX = /(^|[^:])\/{2,}/g
const HTTP_PROTOCOL_REGEX = /^https?:\/\//i
const PROTOCOL_RELATIVE_PREFIX = '//'

/**
 * Safely decodes a URI component, returning the original value if decoding fails.
 */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function isAbsoluteHttpUrl(url: string): boolean {
  const decodedUrl = safeDecode(url)

  return HTTP_PROTOCOL_REGEX.test(decodedUrl) || decodedUrl.startsWith(PROTOCOL_RELATIVE_PREFIX)
}

export function isExternalUrl(url: string): boolean {
  if (isSSR) {
    return isAbsoluteHttpUrl(url)
  }

  try {
    const parsedUrl = new URL(url, window.location.href)

    return parsedUrl.hostname !== window.location.hostname
  } catch {
    return false
  }
}

/**
 * Returns false for mailto:, tel: etc.
 * true for internal and http/https links.
 * @param url a URL
 */
export function isHTTP(url: URL): boolean {
  return ['http:', 'https:'].includes(url.protocol)
}

/**
 * Returns '_blank' if the URL is external (starts with https and not from current host).
 */
export function getLinkTarget(url: string): '_blank' | undefined {
  return isExternalUrl(url) ? '_blank' : undefined
}

/**
 * Returns security rel value when using target="_blank".
 */
export function getLinkRel(url: string): 'noopener noreferrer' | undefined {
  return isExternalUrl(url) ? 'noopener noreferrer' : undefined
}

/**
 * Returns true if the URL is an absolute path or an external URL.
 */
export function isPathOrExternalUrl(url: string): boolean {
  return url.startsWith('/') || isAbsoluteHttpUrl(url)
}

/**
 * Ensures a leading slash for relative URLs, leaving absolute or protocol-relative URLs untouched.
 */
export function ensureLeadingSlash(url: string): string {
  if (!url || isPathOrExternalUrl(url)) {
    return url
  }

  return `/${url}`
}

/**
 * Collapses accidental repeated slashes in a URL/path while preserving protocol markers.
 */
export function collapseDoubleSlashes(url: string): string {
  if (!url) return url

  if (url.startsWith(PROTOCOL_RELATIVE_PREFIX)) {
    const rest = url.slice(PROTOCOL_RELATIVE_PREFIX.length)

    return `${PROTOCOL_RELATIVE_PREFIX}${rest.replace(COLLAPSE_REGEX, '$1/')}`
  }

  return url.replace(COLLAPSE_REGEX, '$1/')
}

/**
 * Returns the correct separator ('?' or '&') for appending query parameters to a URL.
 */
export function getQueryParamsSeparator(url: string): '?' | '&' {
  return url.includes('?') ? '&' : '?'
}
